import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { ProfileService } from '../data-access/user.service';

/**
 * Interceptor function that sets the `Accept-Language` header on TM4 API requests so the backend
 * localizes responses. The language comes from {@link ProfileService.locale}: the user's stored
 * locale when its `language-<locale>` flag is on, otherwise the default. Nothing is set while no
 * language flag is on ({@link ProfileService.languageSupportEnabled}). A request that already
 * carries an explicit `Accept-Language` header is left untouched. Like
 * {@link withCredentialsInterceptor}, it uses an allowList of URL prefixes so the header is only
 * sent to our own API and never leaks to other domains.
 *
 * @param {string[]} [apiUrls=[]] - An array of URL prefixes that should carry the language header.
 * @returns {HttpInterceptorFn} - A function that adds the `Accept-Language` header to matching requests.
 */
export const acceptLanguageInterceptor =
  (apiUrls: string[] = []): HttpInterceptorFn =>
  (req, next) => {
    if (req.headers.has('Accept-Language') || !apiUrls.some(url => req.url.startsWith(url))) {
      return next(req);
    }

    const profileService = inject(ProfileService);
    if (!profileService.languageSupportEnabled()) {
      return next(req);
    }

    return next(
      req.clone({ headers: req.headers.set('Accept-Language', profileService.locale()) })
    );
  };
